import React, {createContext, useContext, useEffect, useState} from 'react';

// Auth state for the whole portal frontend. Polls /api/me on mount; tracks
// three states via the `user` value:
//   - undefined : initial fetch in flight (render loading shell, not login)
//   - null      : confirmed anonymous (render login page)
//   - object    : logged in (render dashboard + user menu)
//
// The undefined-vs-null distinction matters because rendering the login
// page during the brief initial fetch would flicker the auth UI for users
// who actually do have a valid session cookie.

const AuthContext = createContext(null);

export function AuthProvider({children}) {
    const [user, setUser] = useState(undefined);

    useEffect(() => {
        let cancelled = false;
        fetch('/api/me')
            .then((r) => (r.ok ? r.json() : {user: null}))
            .then((d) => {
                if (!cancelled) setUser(d.user || null);
            })
            .catch(() => {
                if (!cancelled) setUser(null);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const logout = async () => {
        try {
            await fetch('/auth/logout', {method: 'POST'});
        } finally {
            // Hard reload guarantees no stale React state survives the
            // session change — the next /api/me fetch will return 401.
            window.location.href = '/';
        }
    };

    return (
        <AuthContext.Provider value={{user, logout}}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be called inside <AuthProvider>');
    }
    return ctx;
}
